import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { analytics } from '@eti/utils';
import {
  useProperty,
  useSearch,
  useSiteContext,
  useSnowplowTracking,
  useTranslation,
} from '@eti/providers';
import { SeatMapModalProvider, MUTUALLY_EXCLUSIVE } from '@eti/products';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import { fillContactForm, fillTravelersForm } from '../../../common/debug/utils';
import { TRAVELER_DETAILS } from '../../../constants/formNamesConstants';
import { getAddedExtraProducts, isSeatingOffered } from '../../../utils/addedExtraProducts';
import { transformData } from '../../../common/utils/transformData';
import { getDomesticInfo } from '../../../common/utils/utils';
import { withTravelerDetailsValidationProvider } from '../components/WithTravelerDetailsValidationProvider';
import { getTravelerNames } from '../../../utils/travelerForm';
import {
  useSearchValuesToDataLayerEffect,
  useSelectedTripToDataLayerEffect,
  useTravelerPricesToDataLayerEffect,
} from '../../../common/data-layer/dataLayer';
import { useSkyscannerLandingTracking } from '../../../common/tracking/skyscannerTracking';
import TravelerDetailsPage from '../components/TravelerDetailsPage';
import { asyncBlurFields, doAsyncFormValidation } from '../utils/asyncFormValidation';
import * as AvailableMuxProductsUtil from '../utils/availableMuxProducts';
import { getAllKindsOfCarrierCodes } from '../../../utils/carriersUtils';
import PhonePrefixChangeConfirmationDialog from '../components/PhonePrefixChangeConfirmationDialog/PhonePrefixChangeConfirmationDialogContainer';
import { ANCILLARY_ITEMS_QUERY } from '../graphql/queries';

const mapStateToProps = (state, { form, travelers }) => {
  return {
    travelerNames: getTravelerNames(form, state, travelers),
    addedProductsNumber: getAddedExtraProducts(form, undefined)(state).length,
  };
};

const mapDispatchToProps = (_dispatch, { travelers, purchasableMuxProducts }) => ({
  fillForm: async (change) => {
    const identifierProductMuxKey =
      MUTUALLY_EXCLUSIVE.generateMuxProductKey(purchasableMuxProducts);
    const muxProduct = identifierProductMuxKey
      ? {
          [identifierProductMuxKey]: MUTUALLY_EXCLUSIVE.getEmptyOptionId(identifierProductMuxKey),
        }
      : {};

    fillContactForm(change);
    await fillTravelersForm(change, travelers, muxProduct);
  },
});

const withReduxForm = () =>
  reduxForm({
    form: TRAVELER_DETAILS,
    touchOnChange: true,
    asyncValidate: doAsyncFormValidation,
    asyncBlurFields,
  });

const composed = () =>
  compose(
    connect(mapStateToProps, mapDispatchToProps),
    withTravelerDetailsValidationProvider,
    withReduxForm(),
  )(
    ({
      addedProductsNumber,
      anyTouched,
      asyncValidate,
      cart,
      change,
      fillForm,
      form,
      handleSubmit,
      headerAndInfoHeight,
      isAncillaryBundlingEnabled,
      reset,
      chargingCurrency,
      submitting,
      travelerNames,
      tripOperatingCarriers,
      valid,
      ...props
    }) => (
      <TravelerDetailsPage
        addedProductsNumber={addedProductsNumber}
        asyncValidate={asyncValidate}
        cart={cart}
        change={change}
        chargingCurrency={chargingCurrency}
        fillForm={fillForm}
        form={form}
        handleSubmit={handleSubmit}
        headerAndInfoHeight={headerAndInfoHeight}
        isAncillaryBundlingEnabled={isAncillaryBundlingEnabled}
        isAnyTouched={anyTouched}
        isSubmitting={submitting}
        isValid={valid}
        reset={reset}
        travelerNames={travelerNames}
        tripOperatingCarriers={tripOperatingCarriers}
        {...props}
      />
    ),
  );

const TravelerDetailsContainer = ({ addToCart, data, headerAndInfoHeight, isLoading }) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const client = useApolloClient();
  const siteContext = useSiteContext();
  const { currentSearchData } = useSearch();
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [tripData, setTripData] = useState(data);
  const hasSnowplowEventBeenCalled = useRef(false);
  const coverGeniusProductIds = p('Ancillary.Items.Fallback.Product.Predicate');
  const isDynamicBookingFlowEnabled = p('TravelerDetails.DynamicFlow.Enabled');

  const [getCoverGeniusProduct] = useLazyQuery(ANCILLARY_ITEMS_QUERY, {
    fetchPolicy: 'no-cache',
    onCompleted: (responsedCoverGeniusProducts) => {
      const selectedTripProductsExcludingCoverGenius =
        data.selectedTrip?.availableExtraProducts?.filter(
          (extraProduct) => !coverGeniusProductIds.includes(Number(extraProduct.id)),
        ) ?? [];

      if (responsedCoverGeniusProducts.length === 0) {
        setTripData(selectedTripProductsExcludingCoverGenius);
        return;
      }

      const selectedTripProductsIncludingAncillaryItemsCoverGenius = [
        ...selectedTripProductsExcludingCoverGenius,
        ...responsedCoverGeniusProducts.ancillaryItems,
      ];

      const updatedTripData = cloneDeep(data);
      updatedTripData.selectedTrip.availableExtraProducts =
        selectedTripProductsIncludingAncillaryItemsCoverGenius;

      setTripData(updatedTripData);
    },
  });

  const { countryCode: domesticCountryCode, isDomestic } = getDomesticInfo(
    currentSearchData.routes,
  );

  useSelectedTripToDataLayerEffect(tripData.selectedTrip);
  useSearchValuesToDataLayerEffect(currentSearchData);
  useTravelerPricesToDataLayerEffect(
    tripData.selectedTrip?.travelerPrices,
    tripData.selectedTrip?.paymentMethodPrices,
  );

  const { isSnowplowTrackingEnabled, updateSnowplowContextData, sendTrackEvent } =
    useSnowplowTracking();

  useSkyscannerLandingTracking(tripData.selectedTrip?.travelerPrices);

  useEffect(() => {
    if (isSnowplowTrackingEnabled) {
      const availableExtraProducts =
        tripData.selectedTrip?.availableExtraProducts ??
        (tripData.selectedTrip?.availableExtraProducts || null);

      const offeredProducts = availableExtraProducts?.map((product) => product.name).join(', ');
      const airSystems =
        tripData.selectedTrip?.systems && tripData.selectedTrip?.systems.join(', ');

      const { marketingCarrierCodes, operatingCarrierCodes, ticketingCarrierCodes } =
        getAllKindsOfCarrierCodes(tripData.selectedTrip?.bounds);

      updateSnowplowContextData({
        flightData: {
          airSystems,
          marketingCarrierCodes,
          operatingCarrierCodes,
          validatingCarrierCodes: ticketingCarrierCodes,
        },
        productData: {
          offeredProducts,
          ancillaryProduct: null,
        },
      });

      if (!hasSnowplowEventBeenCalled.current) {
        sendTrackEvent({
          category: 'Traveler Details',
          action: 'Seen',
          label: 'Page',
        });
        hasSnowplowEventBeenCalled.current = true;
      }
    }
  }, [
    tripData.selectedTrip?.availableExtraProducts,
    tripData.selectedTrip?.systems,
    isSnowplowTrackingEnabled,
    updateSnowplowContextData,
    sendTrackEvent,
    tripData.selectedTrip?.bounds,
  ]);

  const isAncillaryBundlingEnabled = tripData.selectedTrip?.availableAncillaryBundles?.some(
    (bundle) => bundle.selectedOption === null,
  );

  const transformedData = transformData(tripData, siteContext, t, {
    isAncillaryBundlingEnabled,
    isNewCoverGeniusCardsEnabled: p('IbeClient.CoverGeniusV2.Card.Enabled'),
    isDynamicBookingFlowEnabled,
    isProductsPrimarySecondaryEnabled: p('TravelerDetails.ProductsPrimarySecondary.Enabled'),
    primaryProductCodes: p('TravelerDetails.PrimaryProducts'),
    productsPerTravelerExcludedProviderSystems: p(
      'IbeClient.ProductsPerTravelerExcludedProviderSystems',
    ),
    productsSortOrder: p('TravelerDetails.Products.SortOrder'),
    productsToDisplayPerTraveler: p('IbeClient.ProductsToDisplayPerTraveler'),
    shouldShowProductsPerTraveler: p('IbeClient.ShowProductsPerTraveler.Enabled'),
    showConfirmEmail: p('IbeClient.ContactInformation.Email.ShowConfirmEmail'),
    shouldOverridePersuasion: !p(
      'IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled',
    ),
    shouldUsePersistedFormData: p('IbeClient.TravelerDetails.PersistFormData.Enabled'),
    isMutuallyExclusiveEnabled: p('IbeClient.TravelerDetails.MutuallyExclusiveProducts.Enabled'),
    isTravelArrangerEnabled: p('TravelerDetails.TravelArranger.Enabled'),
    formDateInputFormat: p('IbeClient.TravelerInput.dateInputFormat'),
  });

  analytics.useGADataLayerEffect({
    event: 'seatingVisible',
    seatingOffered: isSeatingOffered(transformedData.products) ? 'YES' : 'NO',
  });

  const ConnectedTravelerDetailsPage = useMemo(() => composed(), []);

  const purchasableMuxProducts = AvailableMuxProductsUtil.getPurchasableMuxProducts({
    mutuallyExclusiveProducts: tripData.selectedTrip?.mutuallyExclusiveProducts,
    availableExtraProducts: tripData.selectedTrip?.availableExtraProducts,
  });

  return (
    <SeatMapModalProvider>
      <ConnectedTravelerDetailsPage
        addToCart={addToCart}
        chargingCurrency={tripData.selectedTrip?.chargingCurrencyExchangeRate}
        client={client}
        domesticCountryCode={domesticCountryCode}
        form={TRAVELER_DETAILS}
        headerAndInfoHeight={headerAndInfoHeight}
        isAncillaryBundlingEnabled={isAncillaryBundlingEnabled}
        isDomestic={isDomestic}
        isLoading={isLoading}
        purchasableMuxProducts={purchasableMuxProducts}
        setPhoneNumber={setPhoneNumber}
        {...transformedData}
      />
      <PhonePrefixChangeConfirmationDialog
        coverGeniusProductIds={coverGeniusProductIds}
        getCGProducts={getCoverGeniusProduct}
        phoneNumber={phoneNumber}
      />
    </SeatMapModalProvider>
  );
};

TravelerDetailsContainer.propTypes = {
  addToCart: PropTypes.func,
  data: PropTypes.objectOf(PropTypes.any),
  headerAndInfoHeight: PropTypes.number,
  isLoading: PropTypes.bool,
  onEditPassengerSearch: PropTypes.func,
  searchVariables: PropTypes.shape({
    adults: PropTypes.number,
    cabinClass: PropTypes.string,
    childAges: PropTypes.arrayOf(PropTypes.number),
    destination: PropTypes.string,
    // eslint-disable-next-line react/boolean-prop-naming
    direct: PropTypes.bool,
    endDate: PropTypes.string,
    origin: PropTypes.string,
    routes: PropTypes.arrayOf(
      PropTypes.shape({
        departureAt: PropTypes.number,
        destination: PropTypes.string,
        origin: PropTypes.string,
      }),
    ),
    selectionKey: PropTypes.string,
    startDate: PropTypes.string,
    travelers: PropTypes.string,
  }),
};

export default TravelerDetailsContainer;
